import React, { MouseEventHandler } from "react";

import { ROWS_PER_PAGE } from "@covertree/consts";
import { OptionType } from "@covertree/types";
import Box from "@mui/material/Box";
import TablePagination from "@mui/material/TablePagination";

import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";

import { Table } from "../Table";
import { TableFilters } from "../TableFilters";
import type { Row, Column } from "../types";

export interface TableViewProps {
  header?: string;
  tabs?: Omit<OptionType, "icon">[];
  page: number;
  totalCount: number;
  rows: Row[];
  rowsPerPage?: number;
  columns: Column[];
  onTabChange?: (nextTab: string) => void;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number
  ) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  loading?: boolean;
  error?: boolean;
  onSortChange?: (column: string) => MouseEventHandler;
  onRowsPerPageChange?: (newRowsPerPage: number) => void;
  sequoiaFilter?: {
    selected: boolean;
    onClick: () => void;
  };
  showPagination?: boolean;
  maxTableHeight?: number | string;
}

export const TableView = ({
  header,
  tabs,
  page,
  totalCount,
  rows,
  rowsPerPage,
  columns,
  onTabChange,
  onPageChange,
  sortBy,
  sortOrder,
  loading,
  error,
  onSortChange,
  onRowsPerPageChange,
  sequoiaFilter,
  showPagination = true,
  maxTableHeight,
}: TableViewProps) => {
  return (
    <React.Fragment>
      {header ? (
        <Typography
          variant="h3"
          pl={1.2}
          sx={{ backgroundColor: "background.default" }}>
          {header}
        </Typography>
      ) : null}
      <Box mt={5}>
        {!!tabs && !!onTabChange && (
          <TableFilters tabs={tabs} onChange={onTabChange} loading={loading} />
        )}
        {sequoiaFilter && (
          <Box mt={3} id="table-toolbar-container">
            <Button
              size="small"
              variant={sequoiaFilter.selected ? "contained" : "outlined"}
              color="secondary"
              onClick={sequoiaFilter.onClick}>
              Show Sequoia only
            </Button>
          </Box>
        )}
        <Box mt={sequoiaFilter ? 1 : 4} mx={0.4}>
          <Table
            rows={rows}
            columns={columns}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSortChange={onSortChange}
            error={error}
            loading={loading}
            maxHeight={maxTableHeight}
          />
        </Box>
        <Box mt={0.75}>
          {!error && showPagination && (
            <Box display="flex" flexGrow={1} justifyContent="flex-end">
              <TablePagination
                component="div"
                sx={{
                  boxShadow: "none !important",
                  "& .MuiTablePagination-select": (theme) => ({
                    marginTop: theme.spacing(1.25),
                    fontSize: "1.2rem",
                  }),
                }}
                count={totalCount}
                page={page - 1}
                rowsPerPageOptions={
                  rowsPerPage ? [5, 10, 25, 50, 100] : [ROWS_PER_PAGE]
                }
                rowsPerPage={rowsPerPage ?? ROWS_PER_PAGE}
                onPageChange={(_, page) => onPageChange(null, page + 1)}
                onRowsPerPageChange={(e) => {
                  onRowsPerPageChange?.(parseInt(e.target.value, 10));
                  onPageChange(null, 1);
                }}
              />
            </Box>
          )}
        </Box>
      </Box>
    </React.Fragment>
  );
};
